/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * «Fast Jumu'ah-tid» on Bønnetider (owner 2026-11-01: "we are missing Jumma timing — can you add a permanent [setting]
 * in Bønnetider"): one khutbah (optional) and Jumu'ah jamaat for EVERY Friday. It is the lowest jamaat rule — a day's
 * override, an own period, an IRN period and the year table all win over it — so it fills the Fridays nothing else
 * covers (CJAS: IRN's period ended and had no Jumu'ah). Shown inside «Jamaat-tider» from that card's own answer
 * (GET /prayer-times/jamaat → fixedJumua); set and removed with prayer.write (the server decides: canWrite). Core
 * checks the times first (lib/jamaat checkFixedJumua); the server checks again, also against the Fridays it would be
 * used on (not before Dhuhr begins, not at/after Asr begins), and audits each change.
 */
import React, { useState } from 'react';
import { errText, send, type Body } from '../lib/api';
import { useMsg } from '../lib/hooks';
import { osloTime } from '../lib/format';
import { Btn, Field, Msg, Tag, TwoStep } from '../components/ui';
import { checkFixedJumua, fixedJumuaBody, fixedJumuaForm, fixedJumuaText, type FixedJumuaForm } from '../lib/jamaat';

const PATH = '/prayer-times/jumua-fixed';

export function JumuaFixedSection({ fixed, write, onSaved }: { fixed: Body | null; write: boolean; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState<FixedJumuaForm | null>(null);
  const [bad, setBad] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const set = (patch: Partial<FixedJumuaForm>) => { setForm({ ...(form as FixedJumuaForm), ...patch }); setBad({}); };

  async function save() {
    if (!form) return;
    const c = checkFixedJumua(form);
    if (!c.ok) { setBad(c.byField); return say(c.errors[0].message, 'err'); }
    setBusy(true);
    const r = await send(PATH, { method: 'PUT', body: JSON.stringify(fixedJumuaBody(form)) });
    setBusy(false);
    if (!r.ok) {
      const errs: Body[] = Array.isArray(r.body && r.body.errors) ? r.body.errors : [];
      setBad(Object.fromEntries(errs.filter((e) => e.field).map((e) => [e.field, e.message])));
      return say((r.body && r.body.message) || errText(r.body), 'err');
    }
    setForm(null);
    say("Lagret. Fredager uten annen Jumu'ah-tid får denne i appen, på skjermene og på nettsiden.", 'ok');
    await onSaved();
  }

  async function remove() {
    const r = await send(PATH, { method: 'DELETE' });
    if (!r.ok) return say(errText(r.body), 'err');
    setForm(null);
    say("Den faste Jumu'ah-tiden er fjernet.", 'ok');
    await onSaved();
  }

  const outside = fixed && (fixed.beforeBegins || fixed.afterWindow);
  return (
    <section className="jm-fixed" aria-labelledby="jm-fixed-h" style={{ marginTop: 16 }}>
      <h3 id="jm-fixed-h" style={{ margin: '0 0 4px' }}>Fast Jumu'ah-tid</h3>
      <p className="note" style={{ margin: '0 0 8px' }}>Gjelder alle fredager. En periode, årstabellen eller en overstyring for én dag går foran.</p>
      {!form && (
        <div className="row" style={{ alignItems: 'center', gap: 8 }}>
          <span data-testid="jm-fixed-value">{fixedJumuaText(fixed)}</span>
          {fixed && fixed.updatedAt && <span className="note">Sist endret {osloTime(fixed.updatedAt)}{fixed.updatedBy ? ` av ${fixed.updatedBy}` : ''}</span>}
          {write && <Btn ghost onClick={() => { setForm(fixedJumuaForm(fixed)); setBad({}); }}>{fixed ? 'Endre' : 'Sett tid'}</Btn>}
          {write && fixed && <TwoStep label="Fjern" confirmLabel="Klikk igjen for å fjerne" onConfirm={remove} />}
        </div>
      )}
      {outside && !form && (
        <p><Tag tone="red">{(fixed.beforeBegins || fixed.afterWindow).days} {(fixed.beforeBegins || fixed.afterWindow).days === 1 ? 'fredag' : 'fredager'} utenfor Dhuhr-tiden — vises ikke</Tag>
          {' '}<span className="note">Første {(fixed.beforeBegins || fixed.afterWindow).first}. Legg inn en egen periode for de fredagene.</span></p>
      )}
      {write && form && (
        <fieldset className="jm-form" aria-label="Fast Jumu'ah-tid">
          <div className="row">
            <Field label="Khutbah (valgfritt)" style={{ maxWidth: 160 }} error={bad.jumua_khutbah || null}>{(id, d) =>
              <input id={id} type="text" autoComplete="off" placeholder="13:00" aria-describedby={d} aria-invalid={Boolean(bad.jumua_khutbah) || undefined}
                     value={form.khutbah} onChange={(e) => set({ khutbah: e.target.value })} />}</Field>
            <Field label="Jumu'ah-jamaat" required style={{ maxWidth: 160 }} error={bad.jumua_jamaat || null}>{(id, d) =>
              <input id={id} type="text" autoComplete="off" placeholder="13:30" aria-describedby={d} aria-invalid={Boolean(bad.jumua_jamaat) || undefined}
                     value={form.jamaat} onChange={(e) => set({ jamaat: e.target.value })} />}</Field>
          </div>
          <div className="row">
            <Btn onClick={save} disabled={busy}>{busy ? 'Lagrer …' : 'Lagre'}</Btn>
            <Btn ghost onClick={() => { setForm(null); setBad({}); say('Avbrutt. Ingenting er lagret.'); }}>Avbryt</Btn>
          </div>
        </fieldset>
      )}
      <Msg text={msg.text} tone={msg.tone} />
    </section>
  );
}
